import { createSignal, onCleanup, onMount, Show, type JSX } from 'solid-js';
import { CONSOLE_UPDATE_COPY } from '../lib/consoleUpdateCopy';
import { InlineNotice } from './ui';

const CHECK_INTERVAL_MS = 5 * 60 * 1_000;
const BUNDLED_REF = typeof __PLATFORM_GIT_REF__ === 'string' ? __PLATFORM_GIT_REF__ : '';
const FAILURE_LOG_KEY = 'nuance-console-version-check-failed';
let failureLoggedWithoutStorage = false;

export function ConsoleUpdateNotice(props: { builtRef?: string; reload?: () => void } = {}): JSX.Element {
  const [available, setAvailable] = createSignal(false);
  const [dismissed, setDismissed] = createSignal(false);
  let inFlight = false;
  let loggedFailure = false;
  let mounted = true;

  const check = async (): Promise<void> => {
    if (document.visibilityState !== 'visible' || inFlight || !(props.builtRef ?? BUNDLED_REF)) return;
    inFlight = true;
    try {
      const response = await fetch('/version.json', { cache: 'no-store' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const body: unknown = await response.json();
      const ref = typeof body === 'object' && body !== null && 'ref' in body ? body.ref : undefined;
      if (typeof ref !== 'string' || !/^[0-9a-f]{40}$/.test(ref)) throw new Error('invalid version.json');
      if (mounted && ref !== (props.builtRef ?? BUNDLED_REF) && !dismissed()) setAvailable(true);
    } catch (error) {
      if (!loggedFailure) {
        let loggedInTab = false;
        try {
          loggedInTab = sessionStorage.getItem(FAILURE_LOG_KEY) === '1';
          sessionStorage.setItem(FAILURE_LOG_KEY, '1');
        } catch {
          loggedInTab = failureLoggedWithoutStorage;
          failureLoggedWithoutStorage = true;
        }
        if (!loggedInTab) console.warn('console version check failed', error);
        loggedFailure = true;
      }
    } finally {
      inFlight = false;
    }
  };
  const onVisibility = (): void => { if (document.visibilityState === 'visible') void check(); };
  const onFocus = (): void => { void check(); };

  onMount(() => {
    void check();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    const interval = window.setInterval(() => { void check(); }, CHECK_INTERVAL_MS);
    onCleanup(() => {
      mounted = false;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.clearInterval(interval);
    });
  });

  return (
    <Show when={available()}>
      <InlineNotice tone="info" title={CONSOLE_UPDATE_COPY.title} onDismiss={() => { setDismissed(true); setAvailable(false); }}>
        <button type="button" class="button button-secondary" onClick={() => (props.reload ?? (() => window.location.reload()))()}>
          {CONSOLE_UPDATE_COPY.reloadLabel}
        </button>
      </InlineNotice>
    </Show>
  );
}
